import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { NgRedux, select } from '@angular-redux/store';
import { Observable, Subscription } from 'rxjs';
import { AppState } from '../../../store/state.model';
import { TranslationService } from '../../../common/services/languages/translation.service';
import { VehicleState } from '../../../store/vehicle-store/vehicle-state.model';
import { NgIf, NgFor } from '@angular/common';
import { MatTooltip } from '@angular/material/tooltip';
import { MatDivider } from '@angular/material/divider';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
    selector: 'app-vehicle-package',
    templateUrl: './vehicle-package.component.html',
    styleUrls: ['./vehicle-package.component.scss'],
    imports: [NgIf, NgFor, MatTooltip, MatDivider, TranslatePipe]
})
export class VehiclePackageComponent implements OnInit, OnDestroy {
  /**
  * @property Array of Vehicle  details
  */
  @Input() public vehicleDetails;
  /**
  * @property Array of Vehicle package details to map the details based on product
  */
  @Input() public packages;
  /**
  * @property type of package trial or paid
  */
  @Input() public packageType: string;
  @select('vehicleStore') readonly vehicleObservable: Observable<VehicleState>;
  /**
  * @property wifi status label
  */
  public wifiStatusLabel: string;
  /**
  * @property wifi capable label
  */
  public wifiCapable: boolean;
  /**
  * @property checks if vehicle is 17cy plus
  */
  public is17Plus: boolean;
  /**
  * @property stores vehicle brand
  */
  public vehicleBrand: string;
  /**
  * @property stored the vehicel satus
  */
  public wifiStatus: string;
  /**
    *@property Used to set the brand name to the translations of the translate service
    *@type {object}
    **/
  public translateParams: object;
  /**
   * @property to store the subscriptions
   */
  private subscription: Subscription;

  constructor(
    public translate: TranslationService,
    public reduxState: NgRedux<AppState>
  ) {
    this.subscription = this.vehicleObservable.subscribe((vehicleStore) => {
      this.setTranslateParams();
    });
    this.setTranslateParams();
  }
  ngOnInit() {
    this.vehicleBrand = this.vehicleDetails.brand;
    this.is17Plus = this.vehicleDetails.generation === '17CYPLUS';
  }
  /**
   * ondestroy method
   */
  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }
  /**
    * @function settranslateParams sets the translation parms based on language change
    * @return void
    */
  public setTranslateParams(): void {
    this.translateParams = {
      'brandSpecificName': this.translate.getCustomTranslation(this.vehicleBrand === 'T' ? 'data-consent.label.customer' : 'data-consent.label.guest')['value']
    };
  }
  /**
   * @function getTerm Returns an ProductTermInterface wich will have the values for dispalying and translating the term of the product
   * @param product Contain the valid values of a product
   */
  public getTerm(product: Product): ProductTermOutput {
    const { term, termUnit, subscriptionStartDate, subscriptionEndDate } = product;
    return this.getProductTerm({ term, termUnit }, { subscriptionStartDate, subscriptionEndDate });
  }
  /**
   * @function getProductTerm Calculate the time based on unit for each product
   * @param productTerm ProductTermInput object with the values to change
   * @param dates Product coming from Products API
   */
  public getProductTerm(productTerm: ProductTermInput, dates: Product): ProductTermOutput {
    const { term, termUnit } = productTerm;
    return termUnit === 'Day' ? this.getDays(dates) : this.getMonths(term);
  }
  /**
   * @function getDays Return dates of star and end
   * @param dates Of start and ending for each product of the package
   */
  private getDays(dates: Product): ProductTermOutput {
    return {
      value: {
        startDate: dates.subscriptionStartDate,
        endDate: dates.subscriptionEndDate,
      },
      label: ['vehiclePackage.product-term-units.start-date', 'vehiclePackage.product-term-units.end-date']
    };
  }
  /**
   * @function getMonths Return dates based on a raw date format
   * @param unit Number of months
   */
  private getMonths(unit: number): ProductTermOutput {
    const years = Math.floor(unit / 12);
    const yearMonths = years > 0 ? unit - (12 * years) : 0;
    const labelYM = yearMonths > 0
      ? yearMonths > 1
        ? [`vehiclePackage.product-term-units.years-months`]
        : [`vehiclePackage.product-term-units.years-month`]
      : [`vehiclePackage.product-term-units.years`];
    return {
      value: {
        years: years,
        months: yearMonths > 0 ? yearMonths : unit,
      },
      label: unit > 24 ? labelYM : (unit > 1 ? [`vehiclePackage.product-term-units.months`] : [`vehiclePackage.product-term-units.month`])
    };
  }
  /**
    * @function getPattern gets the translation pattern tci or non-tci based on vehicle region
    */
  public getPattern(join1: string, join2: string): string {
    const vehicleRegion = this.vehicleDetails.region === 'CA' ? 'tci' : '';
    return `${join1}${!!vehicleRegion ? `-${vehicleRegion}` : ''}${!!join2 ? `.${join2}` : ''}`;
  }
  /**
   * @property packageTitle Will return the title of the package
   * @return The title of any of the products of the subscription preview response
   */
  public get packageTitle(): string {
    const brand = this.vehicleDetails.brand;
    return this.packageType === 'trial' ? (brand === 'T' || brand === 'TOY' || brand === 'S' || brand === 'SUB' ? 'vehiclePackage.title-ty' : 'vehiclePackage.title-lx') :
      (brand === 'T' || brand === 'TOY' || brand === 'S' || brand === 'SUB' ? 'vehiclePackage.title-ty-paid' : 'vehiclePackage.title-lx-paid');
  }
}
/**
 *@interface ProductTermInterface validates the product term details
 */
export interface ProductTermInput {
  readonly term: number;
  readonly termUnit: string;
}
/**
 *@interface ProductTermOutput validates the product term details
 *to be used in the template and for use of the translations
 */
export interface ProductTermOutput {
  value: {
    years?: number,
    months?: number,
    startDate?: string,
    endDate?: string,
  };
  label: string[];
}
/**
 *@interface Product validates the product payload
 */
export interface Product {
  readonly packageID?: string;
  readonly packageName?: string;
  readonly productID?: string;
  readonly productName?: string;
  readonly productCode?: string;
  readonly ratePlanID?: string;
  readonly term?: number;
  readonly termUnit?: string;
  readonly description?: string;
  readonly subscriptionStartDate?: string;
  readonly subscriptionEndDate?: string;
  readonly type?: 'Trial' | 'Paid';
  readonly price?: number;
  readonly discount?: number;
  readonly currency?: string;
  readonly renewable?: boolean;
}
